import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

/**
 * HiddenNeedle — clickable Easter-egg needle for the Hunt-the-Needle
 * contest.
 *
 * Architecture: each preset "zone" is defined by
 *   (a) a DOM ANCHOR element on the listing page (looked up by
 *       `data-needle-anchor="..."`), and
 *   (b) a CSS position config `{top|bottom, left|right, width, rotate}`.
 *
 * At mount time, HiddenNeedle queries the DOM for the anchor and portals
 * the needle SVG *into* that anchor with absolute positioning. If the
 * anchor doesn't exist on the current page (e.g. `notes-*` zones when
 * the listing has no notes) the needle silently renders nothing.
 *
 * Adding a new zone = one line in ZONES + a `data-needle-anchor="X"`
 * attribute somewhere in ListingDetail. Admins pick the zone from a
 * dropdown in the contest editor — no code change per contest week.
 */
const ZONES = {
  // ─── Anchored to the front card image ───────────────────────────────
  "image-bottom":     { anchor: "image", css: { top: "84.6%", left: "26%",  width: "24%",  transform: "rotate(-0.8deg)" } },
  "image-top":        { anchor: "image", css: { top: "12.5%", left: "20%",  width: "26%",  transform: "rotate(0.6deg)" } },
  "image-corner-tr":  { anchor: "image", css: { top: "5%",    right: "5%",  width: "22%",  transform: "rotate(-22deg)", transformOrigin: "top right" } },
  "image-corner-bl":  { anchor: "image", css: { bottom: "5%", left: "5%",   width: "22%",  transform: "rotate(18deg)",  transformOrigin: "bottom left" } },

  // ─── Anchored to the listing title ──────────────────────────────────
  "title-underline":  { anchor: "title", css: { bottom: "-4px", left: "35%", width: "22%", transform: "rotate(0deg)" } },

  // ─── Anchored to the price row ──────────────────────────────────────
  "price-underline":  { anchor: "price", css: { bottom: "-5px", left: "20%", width: "22%", transform: "rotate(-0.5deg)" } },

  // ─── Anchored to the seller/actions card ────────────────────────────
  "seller-top-line":  { anchor: "seller", css: { top: "-3px",  left: "40%", width: "18%", transform: "rotate(0deg)" } },
  "seller-corner-tr": { anchor: "seller", css: { top: "6px",   right: "6px", width: "60px", transform: "rotate(-22deg)", transformOrigin: "top right" } },

  // ─── Anchored to Card Details ───────────────────────────────────────
  "details-row-line": { anchor: "details", css: { top: "32%",   left: "48%", width: "20%", transform: "rotate(-0.6deg)" } },

  // ─── Anchored to Notes card (only present when listing has notes) ───
  "notes-corner-tr":  { anchor: "notes",  css: { top: "8px",   right: "10px", width: "60px", transform: "rotate(-22deg)", transformOrigin: "top right" } },
  "notes-corner-bl":  { anchor: "notes",  css: { bottom: "8px", left: "10px", width: "60px", transform: "rotate(24deg)",  transformOrigin: "bottom left" } },
  "notes-underline":  { anchor: "notes",  css: { bottom: "12px", left: "28%", width: "22%", transform: "rotate(0deg)" } },

  // ─── Anchored to Combined Shipping notice ───────────────────────────
  "shipping-line":    { anchor: "shipping", css: { top: "50%",  left: "36%", width: "22%", transform: "translateY(-50%) rotate(0deg)" } },

  // ─── Page-level corner placements (always available) ────────────────
  "page-corner-tl":   { anchor: "page", css: { top: "8px",    left: "8px",   width: "60px", transform: "rotate(24deg)",  transformOrigin: "top left" } },
  "page-corner-br":   { anchor: "page", css: { bottom: "8px", right: "8px",  width: "60px", transform: "rotate(-22deg)", transformOrigin: "bottom right" } },
};

// Public list exposed to admin UI so it can build a dropdown without
// having to import the whole config.
export const NEEDLE_ZONE_OPTIONS = [
  { value: "image-bottom",     label: "Card image — bottom edge" },
  { value: "image-top",        label: "Card image — top edge" },
  { value: "image-corner-tr",  label: "Card image — top-right corner (diagonal)" },
  { value: "image-corner-bl",  label: "Card image — bottom-left corner (diagonal)" },
  { value: "title-underline",  label: "Under the listing title" },
  { value: "price-underline",  label: "Under the price" },
  { value: "seller-top-line",  label: "Seller card — top border" },
  { value: "seller-corner-tr", label: "Seller card — top-right corner (diagonal)" },
  { value: "details-row-line", label: "Card Details — inside a row divider" },
  { value: "notes-corner-tr",  label: "Notes card — top-right corner (only if notes exist)" },
  { value: "notes-corner-bl",  label: "Notes card — bottom-left corner (only if notes exist)" },
  { value: "notes-underline",  label: "Notes card — bottom underline (only if notes exist)" },
  { value: "shipping-line",    label: "Combined Shipping notice line" },
  { value: "page-corner-tl",   label: "Page — top-left corner (diagonal)" },
  { value: "page-corner-br",   label: "Page — bottom-right corner (diagonal)" },
];

// Compound SVG path — same silhouette as the downloadable exports.
// evenodd fill-rule makes the elongated eye a real see-through hole.
const NEEDLE_D = (
  "M 2 2 A 1 1 0 0 0 2 4 L 24 4 L 79.5 3.05 L 79.9 3 L 79.5 2.95 L 24 2 Z "
  + "M 4.65 2.35 L 17.35 2.35 A 0.65 0.65 0 0 1 17.35 3.65 L 4.65 3.65 A 0.65 0.65 0 0 1 4.65 2.35 Z"
);

export default function HiddenNeedle({ listingId, contest }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const observerRef = useRef(null);

  const zone = useMemo(() => {
    if (!contest || !contest.is_week_open) return null;
    if (!contest.target_listing_id || contest.target_listing_id !== listingId) return null;
    return ZONES[contest.target_position] || ZONES["image-bottom"];
  }, [contest, listingId]);

  // Find & bind the anchor element. Uses a MutationObserver so late-mounted
  // anchors (e.g. notes card after listing data loads) are picked up too.
  useEffect(() => {
    if (!zone) { setAnchorEl(null); return; }
    const findAnchor = () => {
      const el = document.querySelector(`[data-needle-anchor="${zone.anchor}"]`);
      if (!el) return null;
      // Ensure absolute-positioned child is anchored to this element.
      const cs = window.getComputedStyle(el);
      if (cs.position === "static") el.style.position = "relative";
      return el;
    };
    const initial = findAnchor();
    if (initial) setAnchorEl(initial);
    // Watch for the anchor to appear later.
    observerRef.current = new MutationObserver(() => {
      const el = findAnchor();
      if (el && el !== anchorEl) setAnchorEl(el);
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true });
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [zone?.anchor]);

  if (!zone || !anchorEl) return null;

  const style = {
    position: "absolute",
    pointerEvents: "auto",
    cursor: "pointer",
    display: "block",
    height: "auto",
    zIndex: 5,
    ...zone.css,
  };

  const needle = (
    <a
      href="/contests/needle-found"
      onClick={(e) => { e.preventDefault(); navigate("/contests/needle-found"); }}
      data-testid="hunt57-hidden-needle"
      aria-label="You found the needle"
      style={style}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 80 6"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", width: "100%", height: "auto" }}
      >
        <path d={NEEDLE_D} fillRule="evenodd" fill="rgba(150,150,160,0.55)" />
      </svg>
    </a>
  );

  return createPortal(needle, anchorEl);
}
